import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Flex,
  Tooltip,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import UserProfile from "./UserProfile";
import {
  BriefcaseBusiness,
  Rocket,
  Calendar,
  CheckCheckIcon,
  Calculator,
  Plus,
  Clock,
  BarChart2,
  File,
  Activity,
  Files,
} from "lucide-react";
import Breadcrumbs from "./Breadcrumbs";
import TabLinks from "./TabLinks";
import useCustomTheme from "../../hooks/useCustomTheme";

const TopHeader = ({ openModal, openTaskDrawer, reportManager }) => {
  const location = useLocation();
  const theme = useCustomTheme();
  const [time, setTime] = useState("");

  // Function to get the formatted date and time (without seconds)
  const getCurrentDateTime = () => {
    const today = new Date();

    // Vérifie si l'utilisateur est sur un écran mobile
    const isMobile = window.innerWidth <= 768;

    // Format complet pour les écrans larges
    const formattedDate = today.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: isMobile ? "short" : "long", // Mois court sur mobile
      day: "numeric",
    });

    const formattedTime = today.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate} - ${formattedTime}`;
  };

  useEffect(() => {
    setTime(getCurrentDateTime());

    const timer = setInterval(() => {
      setTime(getCurrentDateTime());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const getTabLinks = () => {
    if (location.pathname === "/funds/active" || location.pathname === "/funds/onboarding") {
      return [
        { to: "/funds/active", icon: BriefcaseBusiness, label: "Active" },
        { to: "/funds/onboarding", icon: Rocket, label: "Onboarding" },
      ];
    }
    
    if (location.pathname.startsWith("/tasks")) {
      return [
        { to: "/tasks/pending", icon: Clock, label: "Pending" },
        { to: "/tasks/completed", icon: CheckCheckIcon, label: "Completed" },
      ];
    }
    if (location.pathname.startsWith("/reporting")) {
      return [
        { to: "/reporting/overview", icon: BarChart2, label: "Overview" },
        { to: "/reporting/all-reports", icon: File, label: "All reports" },
      ];
    }
    if (location.pathname.startsWith("/monitoring")) {
      return [
        { to: "/monitoring/documents", icon: Files, label: "Documents" },
        { to: "/monitoring/screening", icon: Activity, label: "AML / KYC Screening" },
      ];
    }
    return null;
  };

  const tabLinks = getTabLinks();

  return (
    <Flex
      direction="column"
      as="header"
      px={{ lg: 8, base: 4 }}
      py={4}
      gap={2}
      bg={{ lg: "white", base: "grey.50" }}
      position={"sticky"}
      top="0"
      zIndex={1}
      borderBottomWidth={{ lg: "1px", base: "0px" }}
      borderBottomStyle="solid"
      borderBottomColor="gray.200">
      <Flex
        direction="row"
        w="100%"
        justifyContent={"space-between"}
        alignItems={"center"}>
        {location.pathname === "/" ||
        location.pathname.startsWith("/dashboard") ? (
          <Flex gap={2} alignItems={"center"}>
            <Calendar size={16} color={theme.colors.grey[700]} />
            <Text color={"grey700"} fontWeight={"500"}>
              {time}
            </Text>
          </Flex>
        ) : (
          <Breadcrumbs /> // Breadcrumbs for non-dashboard pages
        )}

        <Flex alignItems="center" gap={1}>
          <Menu placement="auto">
            <MenuButton
              as={IconButton}
              colorScheme="brand"
              size={"md"}
              icon={<Plus size={20} />}
              aria-label="Add options"
            />
            <MenuList>
              <MenuItem onClick={() => openTaskDrawer()}>New task</MenuItem>
              <MenuItem onClick={() => openModal("onboardingProcess")}>
                New onboarding
              </MenuItem>
              <MenuItem onClick={reportManager.openNewReportModal}>
                New report
              </MenuItem>
            </MenuList>
          </Menu>

          <Tooltip label="Currency converter">
            <IconButton
              colorScheme="brand"
              bg={"grey.950"}
              size={"md"}
              onClick={() => openModal("CurrencyConverter")}>
              <Calculator size={20} />
            </IconButton>
          </Tooltip>

          <UserProfile />
        </Flex>
      </Flex>
      {tabLinks && tabLinks.length > 0 && <TabLinks links={tabLinks} />}
    </Flex>
  );
};

export default TopHeader;
